import { Tooltip, centsToUnits } from '@systemeio/ui'
import { useAffiliateDashboardInvoices } from 'modules/affiliate-dashboard/hooks/use-affiliate-dashboard-invoices'
import { AffiliateDashboardFilterInterface } from 'modules/affiliate-dashboard/types/affiliate-dashboard-interface'
import { AffiliateDashboardInvoicesInterface } from 'modules/affiliate-dashboard/types/affiliate-dashboard-invoices-interface'
import {
  InvoiceStateEnum,
  InvoiceStateTitle,
} from 'modules/payment/invoices/enums/invoice-state-enum'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useDownloadInvoiceLink } from 'shared/hooks/use-download-invoice-link'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { usePagination } from 'shared/route-query/hooks/use-pagination'
import { currencyFormat } from 'shared/utils/currency-format'
import { dateFormat, dateTimeFormat } from 'shared/utils/date-time-format'
import { InvoiceDetailsModal } from '../../payment/invoices/components/invoice-details-modal'
import { QuestionMarkIcon } from '@systemeio/ui/icons'

interface AffiliateDashboardInvoicesProps {
  mainFilter: AffiliateDashboardFilterInterface
  setMainFilter: React.Dispatch<React.SetStateAction<AffiliateDashboardFilterInterface>>
}

const AffiliateDashboardInvoices = ({
  mainFilter,
  setMainFilter,
}: AffiliateDashboardInvoicesProps) => {
  const { t } = useLocoTranslation()
  const { user } = useUser()
  const router = useRouter()
  const purchaseInvoiceId = router.query.purchaseInvoiceId as string

  const { limit, setLimit, pagination, setPagination, clearPagination } = usePagination(true)

  const [invoiceId, setInvoiceId] = useState<number | null>(null)

  useEffect(() => {
    if (!isNaN(Number(purchaseInvoiceId))) {
      setInvoiceId(Number(purchaseInvoiceId))
    }
  }, [purchaseInvoiceId])

  const getDownloadInvoiceLink = useDownloadInvoiceLink()

  const { affiliateInvoices, counter } = useAffiliateDashboardInvoices({
    filter: mainFilter,
    pagination,
    limit,
  })

  const closeModal = () => {
    setInvoiceId(null)
  }

  useEffect(() => {
    clearPagination()
  }, [clearPagination, mainFilter])

  const columns: ColumnDefinitionInterface<AffiliateDashboardInvoicesInterface>[] = [
    {
      key: 'invoice',
      header: t('dashboard.affiliate.amount'),
      widthPercent: 30,
      onRender: invoice => (
        <div className={'flex items-center gap-1.5'}>
          {invoice.user.affiliateProgramBrandName ? (
            <Tooltip label={invoice.user.affiliateProgramBrandName} mode={'hover'}>
              <button
                onClick={() => setMainFilter(prev => ({ ...prev, vendorId: invoice.user.id }))}
                className={
                  'ring-brand-primary shrink-0 rounded-full outline-hidden ring-offset-2 focus-visible:ring-2'
                }
              >
                <img
                  alt={invoice.user.affiliateProgramBrandName}
                  className={'h-[32px] w-[32px] rounded-full'}
                  src={invoice.user.avatar}
                />
              </button>
            </Tooltip>
          ) : (
            <button
              onClick={() => setMainFilter(prev => ({ ...prev, vendorId: invoice.user.id }))}
              className={
                'ring-brand-primary shrink-0 rounded-full outline-hidden ring-offset-2 focus-visible:ring-2'
              }
            >
              <img
                alt={invoice.user.affiliateProgramBrandName}
                className={'h-[32px] w-[32px] rounded-full'}
                src={invoice.user.avatar}
              />
            </button>
          )}
          <span>
            {currencyFormat(
              centsToUnits(invoice.invoice.amount),
              invoice.invoice.currency,
              user?.dashboardLocale,
            )}
          </span>
        </div>
      ),
    },
    {
      key: 'invoice',
      header: t('dashboard.affiliate.state.label'),
      widthPercent: 50,
      onRender: invoice => (
        <div className={'flex gap-1'}>
          <span>{t(InvoiceStateTitle[invoice.invoice.state])}</span>
          {invoice.invoice.state === InvoiceStateEnum.unpaid && (
            <QuestionMarkIcon
              className={`${
                !!invoiceId ? `fill-brand-primary` : 'fill-fill-primary'
              } group-focus-visible:fill-brand-primary h-[18px] w-[18px] cursor-pointer`}
              onClick={() => {
                setInvoiceId(invoice.id)
              }}
            />
          )}
        </div>
      ),
    },
    {
      key: 'invoice',
      header: t('global.date'),
      widthPercent: 20,
      onRender: invoice => (
        <Tooltip
          wrapperClassName={'w-full'}
          mode={'hover'}
          label={dateTimeFormat(invoice.invoice.date, {
            timeFormat: user?.timeFormat,
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone,
          })}
        >
          <span className={'w-full truncate'}>
            {dateFormat(invoice.invoice.date, {
              dateFormat: user?.dateFormat,
              timeZone: user?.timezone,
            })}
          </span>
        </Tooltip>
      ),
    },
  ]

  return (
    <>
      <ListingLayout
        counter={counter}
        data={affiliateInvoices}
        title={t('dashboard.affiliate.invoices')}
        type={'compact'}
      >
        <Table
          actions={{
            openLink: [
              {
                caption: t('dashboard.affiliate.download_invoice'),
                name: 'download-invoice',
                openInNew: true,
                setHref: invoice => getDownloadInvoiceLink(invoice.id),
              },
            ],
          }}
          data={affiliateInvoices}
          columns={columns}
          limiter={{ limit, setLimit }}
          paginator={{ pagination, setPagination }}
        />
      </ListingLayout>
      <InvoiceDetailsModal invoiceId={invoiceId} onCloseModal={closeModal} />
    </>
  )
}

export default AffiliateDashboardInvoices
